import React, { createContext, useState, useContext, ReactNode } from 'react';
import { useAuth } from './useAuth';

type DemoBotSelectionContextType = {
  selectedBots: string[];
  setSelectedBots: (bots: string[]) => void;
  isBotSelectionOpen: boolean;
  openBotSelection: () => void;
  closeBotSelection: () => void;
  availableBots: string[];
};

const DemoBotSelectionContext = createContext<DemoBotSelectionContextType | null>(null);

export const DemoBotSelectionProvider = ({ children, availableBots }: { children: ReactNode, availableBots: string[] }) => {
  const auth = useAuth();
  const [selectedBots, setSelectedBots] = useState<string[]>(() => {
    if (auth?.groups.includes('DEMO')) {
      const savedBots = localStorage.getItem('demoSelectedBots');
      return savedBots ? JSON.parse(savedBots) : [];
    }
    return [];
  });
  const [isBotSelectionOpen, setIsBotSelectionOpen] = useState(false);

  const handleSetSelectedBots = (bots: string[]) => {
    setSelectedBots(bots);
    if (auth?.groups.includes('DEMO')) {
      localStorage.setItem('demoSelectedBots', JSON.stringify(bots));
    }
  };

  const openBotSelection = () => setIsBotSelectionOpen(true);
  const closeBotSelection = () => setIsBotSelectionOpen(false);

  const value: DemoBotSelectionContextType = {
    selectedBots,
    setSelectedBots: handleSetSelectedBots,
    isBotSelectionOpen,
    openBotSelection,
    closeBotSelection,
    availableBots,
  };

  return (
    <DemoBotSelectionContext.Provider value={value}>
      {children}
    </DemoBotSelectionContext.Provider>
  );
};

export const useDemoBotSelection = () => {
  const context = useContext(DemoBotSelectionContext);
  if (!context) {
    throw new Error('useDemoBotSelection must be used within a DemoBotSelectionProvider');
  }
  return context;
};